import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api, { apiFoutmelding } from '../api/client'
import { i18nGlobal } from '../i18n'
import { useNotificationStore } from './notifications'
import type { FigmaFile, FigmaNode, FigmaTextStyle, FigmaColorStyle, DesignSpecs, CodeSnippet } from '../types'

const t = (key: string) => i18nGlobal.t(key)

interface SyncStatus {
  fileId: number
  status: 'syncing' | 'success' | 'error'
  message?: string
  fileName?: string
}

/** Tussenstand van een lopende sync, voor de voortgangspopover. */
export interface SyncProgress {
  fileId: number
  fileName: string
  fase: 'ophalen' | 'verwerken' | 'afronden'
  verwerkt: number | null
  totaal: number | null
}

export const useFigmaStore = defineStore('figma', () => {
  // State
  const files = ref<FigmaFile[]>([])
  const selectedFile = ref<FigmaFile | null>(null)
  const selectedNode = ref<FigmaNode | null>(null)
  const nodeSpecs = ref<DesignSpecs | null>(null)
  const codeSnippets = ref<Record<string, string>>({})
  const textStylesByFile = ref<Record<number, FigmaTextStyle[]>>({})
  const colorStylesByFile = ref<Record<number, FigmaColorStyle[]>>({})
  const syncStatus = ref<SyncStatus | null>(null)
  const syncProgress = ref<SyncProgress | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isConnected = ref(false)
  /**
   * Waarom er geen koppeling is. 'disconnected' = iemand heeft zelf op
   * ontkoppelen gedrukt, 'expired' = Figma wees de koppeling af. Alleen het
   * tweede is een probleem, en alleen daarbij hoort een waarschuwing.
   */
  const disconnectReason = ref<'disconnected' | 'expired' | null>(null)
  const subscribedChannel = ref<string | null>(null)

  // Getters
  const sortedFiles = computed(() => 
    [...files.value].sort((a, b) => 
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )
  )

  const isSyncing = computed(() => syncStatus.value?.status === 'syncing')

  // Actions
  async function checkConnectionStatus() {
    try {
      const response = await api.get('/figma/status')
      isConnected.value = response.data.connected
      disconnectReason.value = response.data.connected ? null : (response.data.reason ?? 'expired')
      return response.data
    } catch (err) {
      // Een mislukte statuscheck zegt niets over de reden; niet gokken.
      isConnected.value = false
      return { connected: false }
    }
  }

  async function connectFigma() {
    try {
      // NB: de api-client camelizet responses — auth_url heet hier authUrl
      // (auth_url gaf undefined → de popup opende een leeg wit scherm).
      const response = await api.get('/figma/connect')
      const popup = window.open(response.data.authUrl, 'figma_oauth', 'width=500,height=700')

      /*
       * Drie signalen dat het koppelen klaar kan zijn. Ze vullen elkaar aan,
       * want elk kan afzonderlijk uitblijven:
       *
       * 1. postMessage vanaf de callback-pagina — het snelste signaal, maar
       *    het vereist dat window.opener overeind blijft. Bij een geblokkeerde
       *    popup, of wanneer de browser de omleiding in hetzelfde tabblad
       *    afhandelt, komt dat bericht nooit.
       * 2. het koppelvenster gaat dicht — werkt ook zonder opener.
       * 3. dit venster krijgt de focus terug — vangnet als er helemaal geen
       *    popup geopend kon worden.
       *
       * Op alleen signaal 1 bleef het lampje op "niet gekoppeld" staan tot je
       * de pagina handmatig verversde.
       *
       * Elk signaal doet niet meer dan opnieuw de status opvragen. Dat is
       * idempotent, dus een vals alarm (focus terwijl het toestemmingsscherm
       * nog openstaat) kost hooguit één extra verzoek. Pas wanneer de status
       * daadwerkelijk "gekoppeld" is stoppen we met luisteren; anders na vijf
       * minuten, zodat er nooit een luisteraar blijft hangen.
       */
      let stopped = false

      const stop = () => {
        if (stopped) return
        stopped = true
        window.removeEventListener('message', onMessage)
        window.removeEventListener('focus', onFocus)
        clearInterval(closedTimer)
        clearTimeout(giveUpTimer)
      }

      const refresh = async () => {
        if (stopped) return
        const status = await checkConnectionStatus()
        if (status?.connected) stop()
      }

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return
        if (event.data?.type === 'figma-oauth') refresh()
      }

      const onFocus = () => refresh()

      const closedTimer = setInterval(() => {
        if (popup?.closed) refresh()
      }, 1000)

      const giveUpTimer = setTimeout(stop, 5 * 60 * 1000)

      window.addEventListener('message', onMessage)
      window.addEventListener('focus', onFocus)
    } catch (err: any) {
      error.value = apiFoutmelding(err, t('figma.errors.connectFailed'))
      throw err
    }
  }

  async function disconnectFigma() {
    try {
      await api.delete('/figma/disconnect')
      isConnected.value = false
      disconnectReason.value = 'disconnected'
    } catch (err: any) {
      error.value = apiFoutmelding(err, t('figma.errors.disconnectFailed'))
      throw err
    }
  }

  async function fetchFiles() {
    isLoading.value = true
    error.value = null

    try {
      // De backend pagineert standaard op 20; dit overzicht heeft geen
      // paginering en filtert client-side over de volledige lijst. Zonder
      // per_page zag een klant met meer dan 20 bestanden alleen de twintig
      // meest recente - de rest was niet te openen, te synchroniseren of in
      // te sluiten, zonder dat er iets op wees. Starter mag er 50, Team 200.
      const response = await api.get('/figma/files', { params: { per_page: 500 } })
      files.value = response.data.data
    } catch (err: any) {
      error.value = apiFoutmelding(err, t('figma.errors.filesFailed'))
    } finally {
      isLoading.value = false
    }
  }

  async function importFile(url: string, options?: { name?: string; autoSync?: boolean }) {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post('/figma/files/import', {
        figma_url: url,
        name: options?.name,
        auto_sync: options?.autoSync ?? true,
      })
      
      const newFile = response.data.file
      files.value.push(newFile)
      
      return newFile
    } catch (err: any) {
      error.value = apiFoutmelding(err, t('figma.errors.importFailed'))
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function fetchFile(fileId: number, options?: { includeSpecs?: boolean; compact?: boolean }) {
    isLoading.value = true
    error.value = null

    try {
      // include_specs=false scheelt megabytes aan payload bij grote bestanden;
      // specs per node worden toch los opgehaald via fetchNodeSpecs.
      //
      // compact=true geeft per element alleen naam, type en miniatuur. Dat is
      // precies wat de lijstweergave toont, en scheelt bij een groot bestand
      // een factor zes (16 MB tegen 2,5 MB bij 33.000 elementen).
      const params: Record<string, string> = {}
      if (options?.includeSpecs === false) params.include_specs = 'false'
      if (options?.compact) params.compact = 'true'

      const response = await api.get(`/figma/files/${fileId}`, {
        params: Object.keys(params).length > 0 ? params : undefined,
      })
      selectedFile.value = response.data.file
      return response.data.file
    } catch (err: any) {
      error.value = apiFoutmelding(err, t('figma.errors.fileFailed'))
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Viewer: haal alleen de top-level frames (de schermlijst) op.
   * Voor grote bestanden scheelt dit het downloaden van tienduizenden nodes.
   * `silent` slaat de globale isLoading over — voor achtergrondverversing
   * tijdens een lopende sync, zonder full-page-loader.
   */
  async function fetchFileFrames(fileId: number, options?: { silent?: boolean }) {
    if (!options?.silent) isLoading.value = true
    error.value = null

    try {
      const response = await api.get(`/figma/files/${fileId}`, {
        params: { top_level: 'true', include_specs: 'false' },
      })
      selectedFile.value = response.data.file
      return response.data.file as FigmaFile
    } catch (err: any) {
      error.value = apiFoutmelding(err, t('figma.errors.fileFailed'))
      throw err
    } finally {
      if (!options?.silent) isLoading.value = false
    }
  }

  /**
   * De tekststijlen van een bestand ("Heading/H1" met bijbehorende
   * typografie). Wordt gecachet: de kiezer en de blokvoorbeelden in de
   * editor vragen dezelfde lijst vaak kort na elkaar op.
   */
  async function fetchTextStyles(fileId: number, options?: { refresh?: boolean }) {
    const cached = textStylesByFile.value[fileId]
    if (cached && !options?.refresh) return cached

    const response = await api.get(`/figma/files/${fileId}/text-styles`)
    const styles = (response.data.data ?? []) as FigmaTextStyle[]
    textStylesByFile.value = { ...textStylesByFile.value, [fileId]: styles }
    return styles
  }

  /**
   * De kleurstijlen van een bestand ("Colours/Primary" met bijbehorende
   * hex/rgb-waarde). Gecachet, zelfde reden als fetchTextStyles.
   */
  async function fetchColorStyles(fileId: number, options?: { refresh?: boolean }) {
    const cached = colorStylesByFile.value[fileId]
    if (cached && !options?.refresh) return cached

    const response = await api.get(`/figma/files/${fileId}/color-styles`)
    const styles = (response.data.data ?? []) as FigmaColorStyle[]
    colorStylesByFile.value = { ...colorStylesByFile.value, [fileId]: styles }
    return styles
  }

  /**
   * Eén ingesloten ontwerp, voor een bezoeker zonder account
   * (SAAS_PLAN.md §15h).
   *
   * De gewone weg laadt het hele bestand en zit achter een login. Die route
   * blijft zoals hij is: een bezoeker hoort niet door andermans Figma-bestand
   * te kunnen bladeren. Deze haalt precies het ontwerp op waarop hij klikte,
   * en de server controleert of dat ontwerp is ingesloten op een pagina die
   * hij mag zien.
   *
   * Het antwoord wordt in dezelfde vorm gezet als een geladen bestand, met dat
   * ene ontwerp als enige scherm. Daardoor werkt de viewer eromheen
   * ongewijzigd: de schermlijst is leeg, dus er is niets te bladeren, en het
   * canvas en het specificatiepaneel doen gewoon hun werk.
   */
  async function fetchPublicDesign(nodeId: number) {
    const { data } = await api.get(`/designs/${nodeId}`)
    const lagen = (data.nodes ?? []) as FigmaNode[]

    // Het frame zelf zit in die lagen (loadFrameSubtree begint ermee), dus er
    // is geen apart veld voor nodig.
    const frame = lagen.find((node) => node.id === data.design.id)

    selectedFile.value = {
      id: data.design.fileId,
      name: data.design.name,
      // Bewust leeg: de knop "openen in Figma" hoort niet bij een bezoeker,
      // en de viewer verbergt hem vanzelf zonder adres.
      figmaUrl: null,
      syncStatus: 'success',
      nodes: frame ? [frame] : [],
    } as unknown as FigmaFile

    return lagen
  }

  /**
   * De specs van één aangeklikte laag binnen een publiek ontwerp. Het
   * ontwerp-id gaat mee in het pad: de server beantwoordt daarmee dezelfde
   * toegangsvraag als bij het ontwerp zelf.
   */
  async function fetchPublicNodeSpecs(designId: number, nodeId: number) {
    const { data } = await api.get(`/designs/${designId}/specs/${nodeId}`)
    nodeSpecs.value = data.specs
    return data.specs
  }

  /**
   * Viewer: haal de subtree (frame + nakomelingen) van één frame op.
   * Pas nodig zodra een frame daadwerkelijk geopend wordt.
   */
  async function fetchFrameNodes(fileId: number, frameId: number) {
    const response = await api.get(`/figma/files/${fileId}`, {
      params: { frame_id: frameId, include_specs: 'false' },
    })
    return (response.data.file.nodes ?? []) as FigmaNode[]
  }

  /**
   * Viewer: de binnenkant van alle component-instanties op één scherm,
   * live opgehaald bij Figma (de sync slaat die lagen niet op). De
   * teruggegeven nodes hebben negatieve id's en dragen hun specs inline;
   * ze worden gemarkeerd met het frame waar ze bij horen, zodat de
   * code-tab het juiste endpoint kan aanroepen.
   */
  async function fetchFrameInteriors(frameId: number) {
    const response = await api.get(`/figma/nodes/${frameId}/interiors`)
    const nodes = (response.data.nodes ?? []) as FigmaNode[]
    for (const node of nodes) node.interiorOfFrameId = frameId
    return nodes
  }

  /**
   * Code voor een on-demand geladen instantie-laag: die bestaat niet als
   * databaserij, dus de specs worden server-side uit de interiors-cache
   * gelezen (zie backend interiorNodeCode).
   */
  async function fetchInteriorCode(frameId: number, figmaNodeId: string, format: string) {
    try {
      const response = await api.post(`/figma/nodes/${frameId}/interior-code`, {
        figma_node_id: figmaNodeId,
        format,
      })
      codeSnippets.value[format] = response.data.code
      return response.data.code
    } catch (err: any) {
      error.value = apiFoutmelding(err, t('figma.errors.codeFailed'))
      throw err
    }
  }

  /**
   * Haal de preview van één node direct (synchroon) op bij Figma en zet
   * hem op de node. Vangnet voor de viewer: de detailweergave hoeft zo
   * niet te wachten op achtergrondjobs (of een gefaalde batch).
   */
  async function fetchNodeThumbnail(node: FigmaNode) {
    const response = await api.post(`/figma/nodes/${node.id}/thumbnail`)
    node.thumbnailUrl = response.data.thumbnailUrl ?? null
    return node.thumbnailUrl
  }

  async function syncFile(fileId: number) {
    syncStatus.value = { fileId, status: 'syncing' }
    error.value = null

    try {
      await api.post(`/figma/files/${fileId}/sync`)
      // Status will be updated via WebSocket
    } catch (err: any) {
      syncStatus.value = { 
        fileId, 
        status: 'error', 
        message: apiFoutmelding(err, t('figma.errors.syncFailed')) 
      }
      throw err
    }
  }

  async function deleteFile(fileId: number) {
    try {
      await api.delete(`/figma/files/${fileId}`)
      files.value = files.value.filter(f => f.id !== fileId)
      
      if (selectedFile.value?.id === fileId) {
        selectedFile.value = null
      }
    } catch (err: any) {
      error.value = apiFoutmelding(err, t('figma.errors.deleteFailed'))
      throw err
    }
  }

  async function fetchNodeSpecs(nodeId: number) {
    try {
      const response = await api.get(`/figma/nodes/${nodeId}/specs`)
      nodeSpecs.value = response.data.specs
      return response.data.specs
    } catch (err: any) {
      error.value = apiFoutmelding(err, t('figma.errors.specsFailed'))
      throw err
    }
  }

  async function fetchCodeSnippet(nodeId: number, format: string) {
    try {
      const response = await api.get(`/figma/nodes/${nodeId}/code/${format}`)
      codeSnippets.value[format] = response.data.code
      return response.data.code
    } catch (err: any) {
      error.value = apiFoutmelding(err, t('figma.errors.codeFailed'))
      throw err
    }
  }

  async function generateAllCode(nodeId: number) {
    try {
      await api.post(`/figma/nodes/${nodeId}/generate-code`)
    } catch (err: any) {
      error.value = apiFoutmelding(err, t('figma.errors.codeGenerateFailed'))
      throw err
    }
  }

  // WebSocket event handlers
  function handleSyncStarted(data: { file_id: number; file_name?: string }) {
    syncStatus.value = { fileId: data.file_id, status: 'syncing', fileName: data.file_name }
    syncProgress.value = {
      fileId: data.file_id,
      fileName: data.file_name ?? '',
      fase: 'ophalen',
      verwerkt: null,
      totaal: null,
    }
  }

  function handleSyncProgress(data: {
    file_id: number
    file_name: string
    fase: SyncProgress['fase']
    verwerkt: number | null
    totaal: number | null
  }) {
    syncProgress.value = {
      fileId: data.file_id,
      fileName: data.file_name,
      fase: data.fase,
      verwerkt: data.verwerkt,
      totaal: data.totaal,
    }
  }

  function handleSyncCompleted(data: { file_id: number; file_name?: string; processed_nodes: number }) {
    syncStatus.value = {
      fileId: data.file_id,
      status: 'success',
      message: `${data.processed_nodes} nodes verwerkt`
    }
    syncProgress.value = null

    // Klaar-melding als toast: die is globaal zichtbaar, dus ook als je
    // intussen ergens anders in Pentrical zit.
    useNotificationStore().success(
      i18nGlobal.t('figma.syncPopover.doneTitle'),
      i18nGlobal.t('figma.syncPopover.doneBody', {
        name: data.file_name ?? '',
        count: data.processed_nodes,
      })
    )

    // Refresh the file list
    fetchFiles()

    // Clear status after 5 seconds
    setTimeout(() => {
      if (syncStatus.value?.fileId === data.file_id) {
        syncStatus.value = null
      }
    }, 5000)
  }

  function handleSyncFailed(data: { file_id: number; file_name?: string; error_message: string }) {
    syncStatus.value = {
      fileId: data.file_id,
      status: 'error',
      message: data.error_message
    }
    syncProgress.value = null

    useNotificationStore().error(
      i18nGlobal.t('figma.syncPopover.failedTitle'),
      i18nGlobal.t('figma.syncPopover.failedBody', { name: data.file_name ?? '' })
    )
  }

  // Initialize WebSocket listeners
  // Wordt aangeroepen vanuit App.vue zodra de gebruiker bekend is. Het
  // kanaal wordt onthouden zodat een herhaalde aanroep (bv. na opnieuw
  // inloggen) niet nóg een set handlers op hetzelfde kanaal stapelt.
  function initializeWebSocket(userId: number) {
    if (typeof window === 'undefined' || !(window as any).Echo) return
    if (subscribedChannel.value === `user.${userId}.figma`) return

    leaveChannel()

    const channel = `user.${userId}.figma`
    ;(window as any).Echo.private(channel)
      .listen('.figma.file.syncing', handleSyncStarted)
      .listen('.figma.sync.progress', handleSyncProgress)
      .listen('.figma.file.synced', handleSyncCompleted)
      .listen('.figma.file.sync_failed', handleSyncFailed)

    subscribedChannel.value = channel
  }

  function leaveChannel() {
    if (typeof window === 'undefined' || !(window as any).Echo) return
    if (!subscribedChannel.value) return

    ;(window as any).Echo.leave(subscribedChannel.value)
    subscribedChannel.value = null
  }

  // Clean up
  function cleanup() {
    leaveChannel()

    selectedFile.value = null
    selectedNode.value = null
    nodeSpecs.value = null
    codeSnippets.value = {}
    syncStatus.value = null
  }

  return {
    // State
    files,
    selectedFile,
    selectedNode,
    nodeSpecs,
    codeSnippets,
    textStylesByFile,
    colorStylesByFile,
    syncStatus,
    syncProgress,
    isLoading,
    error,
    isConnected,
    disconnectReason,

    // Getters
    sortedFiles,
    isSyncing,

    // Actions
    checkConnectionStatus,
    connectFigma,
    disconnectFigma,
    fetchFiles,
    importFile,
    fetchFile,
    fetchFileFrames,
    fetchTextStyles,
    fetchColorStyles,
    fetchFrameNodes,
    fetchPublicDesign,
    fetchPublicNodeSpecs,
    fetchFrameInteriors,
    fetchInteriorCode,
    fetchNodeThumbnail,
    syncFile,
    deleteFile,
    fetchNodeSpecs,
    fetchCodeSnippet,
    generateAllCode,
    initializeWebSocket,
    cleanup,
  }
})
